import { Spinner } from '@consigli/facade';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@consigli/shadcn';
import { useLocation } from 'react-router';
import { Link } from 'react-router';

import { BackofficeRoutes } from '@/route-names';

import { useBreadcrumbContext } from './breadcrumb-context';

interface BreadcrumbEntry {
  label: string;
  href?: string;
  isLoading?: boolean;
  isCurrentPage?: boolean;
}

const routeLabels: Record<string, string> = {
  [BackofficeRoutes.PROJECTS]: 'Projects',
  [BackofficeRoutes.ORGANIZATIONS]: 'Organizations',
  [BackofficeRoutes.USERS]: 'Users',
  [BackofficeRoutes.REPORT_TEMPLATES]: 'Report Templates',
};

/**
 * Check if a path segment looks like a dynamic ID (numeric or UUID-like)
 * rather than a static route name.
 */
const isIdSegment = (segment: string): boolean => {
  // Numeric IDs
  if (/^\d+$/.test(segment)) return true;
  // UUIDs (with or without dashes)
  if (/^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(segment))
    return true;
  return false;
};

/**
 * Path segments that are route prefixes only — no standalone page exists.
 * These render as plain text (not clickable) in the breadcrumb.
 */
const nonNavigableSegments = new Set(['Packages', 'Layouts', 'Buildings']);

export const DynamicBreadcrumb = () => {
  const location = useLocation();
  const { segmentNames } = useBreadcrumbContext();

  const generateBreadcrumbs = (): BreadcrumbEntry[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbEntry[] = [];

    // Always start with Home
    breadcrumbs.push({
      label: 'Home',
      href: '/',
    });

    // Build breadcrumbs from path segments
    let currentPath = '';
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];

      currentPath += `/${segment}`;

      const resolvedName = segmentNames[segment];
      const staticLabel = routeLabels[segment];

      // If this is an ID segment that hasn't been resolved yet, show loading
      if (isIdSegment(segment) && !resolvedName) {
        if (i === pathSegments.length - 1) {
          breadcrumbs.push({ label: '', isLoading: true });
        } else {
          breadcrumbs.push({ label: '', href: currentPath, isLoading: true });
        }
        continue;
      }

      const label =
        resolvedName || staticLabel || segment.charAt(0).toUpperCase() + segment.slice(1);

      // If this is the last segment or a non-navigable prefix, render without a link
      if (i === pathSegments.length - 1) {
        breadcrumbs.push({ label, isCurrentPage: true });
      } else if (nonNavigableSegments.has(segment)) {
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label, href: currentPath });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumb if we're only on the dashboard
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <Breadcrumb className="mb-6">
      <BreadcrumbList>
        {breadcrumbs.map((item, index) => (
          <div key={item.href ?? item.label ?? `loading-${index}`} className="flex items-center">
            <BreadcrumbItem>
              {item.isLoading ? (
                <Spinner size="xsmall" />
              ) : item.href ? (
                <BreadcrumbLink asChild>
                  <Link to={item.href}>{item.label}</Link>
                </BreadcrumbLink>
              ) : item.isCurrentPage ? (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              ) : (
                <span className="text-muted-foreground">{item.label}</span>
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </div>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
